import { useAdminAppManagers } from '../useAdminAppManagers'
import { useAdminAppState } from '../useAdminAppState'
import { useAdminAppOrchestration } from './useAdminAppOrchestration'
import {
  useAdminAuthSlice,
  useAdminBuilderSlice,
  useAdminNavigationSlice,
  useAdminPagesSlice,
  useAdminProfileSlice,
  useAdminSecuritySlice,
  useAdminSlidesSlice,
} from './useAdminAppSlices'

type Params = {
  state: ReturnType<typeof useAdminAppState>
  managers: ReturnType<typeof useAdminAppManagers>
  auth: Pick<ReturnType<typeof useAdminAppOrchestration>, 'isAuthenticated' | 'handleLogin' | 'logout'>
  currentSession: ReturnType<typeof useAdminAppOrchestration>['currentSession']
}

export const useAdminAppSlicesComposer = ({ state, managers, auth, currentSession }: Params) => {
  const {
    authLoading,
    sessionBootstrapLoading,
    loginForm,
    setLoginForm,
    loginStage,
    setLoginStage,
    token,
    requiresDeviceTrust,
    setRequiresDeviceTrust,
    section,
    setSection,
    selectedUserId,
    setSelectedUserId,
    selectedShopProductId,
    setSelectedShopProductId,
  } = state

  const { slidesManager, pagesManager, builderManager, profileManager, securityManager } = managers

  const authSlice = useAdminAuthSlice({
    isAuthenticated: auth.isAuthenticated,
    requiresDeviceTrust,
    setRequiresDeviceTrust,
    authLoading,
    sessionBootstrapLoading,
    loginForm,
    setLoginForm,
    loginStage,
    setLoginStage,
    handleLogin: auth.handleLogin,
    token,
    logout: auth.logout,
  })

  const navigation = useAdminNavigationSlice({
    section,
    setSection,
    selectedUserId,
    setSelectedUserId,
    selectedShopProductId,
    setSelectedShopProductId,
  })

  const slides = useAdminSlidesSlice({
    slides: slidesManager.slides,
    loading: slidesManager.loading,
    slideForm: slidesManager.slideForm,
    setSlideForm: slidesManager.setSlideForm,
    editingId: slidesManager.editingId,
    upsertSlide: slidesManager.upsertSlide,
    resetSlideForm: slidesManager.resetSlideForm,
    deleteSlide: slidesManager.deleteSlide,
    selectForEdit: slidesManager.selectForEdit,
    onDropSlide: slidesManager.onDropSlide,
    onDragStart: slidesManager.onDragStart,
  })

  const pages = useAdminPagesSlice({
    pages: pagesManager.pages,
    setPages: pagesManager.setPages,
    openCreatePage: pagesManager.openCreatePage,
    openEditPage: pagesManager.openEditPage,
    handlePageSaved: pagesManager.handlePageSaved,
    handlePageCancel: pagesManager.handlePageCancel,
    pageEditorMode: pagesManager.pageEditorMode,
    pageEditorId: pagesManager.pageEditorId,
  })

  const builder = useAdminBuilderSlice({
    builderPage: builderManager.builderPage,
    builderLoading: builderManager.builderLoading,
    openPageInBuilder: builderManager.openPageInBuilder,
    saveBuilderPage: builderManager.saveBuilderPage,
    setBuilderPage: builderManager.setBuilderPage,
  })

  const profile = useAdminProfileSlice({
    profile: profileManager.profile,
    account: profileManager.account,
    profileLoading: profileManager.profileLoading,
    updateProfile: profileManager.updateProfile,
    changePassword: profileManager.changePassword,
    verifyCurrentPassword: profileManager.verifyCurrentPassword,
    verifyTwoFactorFactor: profileManager.verifyTwoFactorFactor,
    setupTwoFactor: profileManager.setupTwoFactor,
    verifyTwoFactor: profileManager.verifyTwoFactor,
    disableTwoFactor: profileManager.disableTwoFactor,
    regenerateTwoFactorRecoveryCodes: profileManager.regenerateTwoFactorRecoveryCodes,
    acknowledgeTwoFactorRecoveryCodes: profileManager.acknowledgeTwoFactorRecoveryCodes,
    loadPasskeys: profileManager.loadPasskeys,
    registerPasskey: profileManager.registerPasskey,
    removePasskey: profileManager.removePasskey,
  })

  const security = useAdminSecuritySlice({
    sessions: securityManager.sessions,
    sessionsLoading: securityManager.sessionsLoading,
    loadSessions: securityManager.loadSessions,
    trustSession: securityManager.trustSession,
    revokeSession: securityManager.revokeSession,
    teamMembers: securityManager.users,
    roleDefinitions: securityManager.roles,
    capabilityDefinitions: securityManager.capabilities,
    sectionDefinitions: securityManager.sections,
    teamLoading: securityManager.teamLoading,
    loadTeam: securityManager.loadTeam,
    createUser: securityManager.createUser,
    createRole: securityManager.createRole,
    updateUserRoles: securityManager.updateUserRoles,
    currentSession,
  })

  return {
    auth: authSlice,
    navigation,
    slides,
    pages,
    builder,
    profile,
    security,
  }
}
